/**
 * CategorizationSheet — Transaction detail + recategorization
 *
 * Stacked rows layout: Hero → structured rows → category chips → footer.
 * Calls categorizeTransaction on chip tap.
 */

import { useState } from 'react';
import { T } from '@/styles/tokens';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { MerchantAvatar } from '@/components/money/MerchantAvatar';
import { categorizeTransaction } from '@/services/categorizeTransaction';
import { useAuth } from '@/contexts/AuthContext';
import type { ArtTransaction } from '@/hooks/useArtTransactions';

interface CategorizationSheetProps {
  transaction: ArtTransaction | null;
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES = [
  { key: 'ART_SUPPLY', label: 'Art Supply' },
  { key: 'BUSINESS_EXPENSE', label: 'Business' },
  { key: 'ART_INCOME', label: 'Art Income' },
  { key: 'PERSONAL', label: 'Not Art' },
];

function formatLongDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatAmount(cents: number): string {
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const remainder = abs % 100;
  const sign = cents >= 0 ? '\u2212' : '+'; // Plaid: positive = debit, negative = credit
  if (remainder === 0) return `${sign}$${dollars.toLocaleString()}`;
  return `${sign}$${dollars.toLocaleString()}.${String(remainder).padStart(2, '0')}`;
}

export function CategorizationSheet({ transaction, isOpen, onClose }: CategorizationSheetProps) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  if (!transaction) return null;

  const handleSelect = async (category: string) => {
    if (!user || saving) return;
    setSaving(true);
    try {
      await categorizeTransaction(user.uid, transaction.id, category);
      setToast('Updated');
      setTimeout(() => {
        setToast(null);
        onClose();
      }, 800);
    } catch (err) {
      console.error('[CategorizationSheet] Error:', err);
      setToast('Failed to update');
      setTimeout(() => setToast(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const isIncome = transaction.tpCategory === 'ART_INCOME';

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}>

        {/* Hero row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '4px 24px 20px',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 14,
            background: T.cream, border: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, overflow: 'hidden',
          }}>
            <MerchantAvatar
              merchantName={transaction.merchantName}
              logoUrl={transaction.merchantLogoUrl}
              category={transaction.tpCategory}
              size={32}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, fontFamily: T.fontFamily }}>
              {transaction.merchantName}
            </div>
            <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.fontFamily, marginTop: 1 }}>
              {formatLongDate(transaction.date)}
            </div>
          </div>
          <div style={{
            fontSize: 26, fontWeight: 700, color: isIncome ? T.primary : T.text,
            letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
            fontFamily: T.fontFamily, flexShrink: 0,
          }}>
            {formatAmount(transaction.amount)}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: T.border }} />

        {/* Structured rows */}
        <div>
          {/* Source */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 24px', borderBottom: `1px solid ${T.borderLight}`,
          }}>
            <span style={{ fontSize: 13, color: T.textMuted }}>Source</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: T.text }}>
              {transaction.source === 'manual' ? 'Manual' : 'Plaid'}
            </span>
          </div>

          {/* Reason (optional) */}
          {transaction.tpReason && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 24px', borderBottom: `1px solid ${T.borderLight}`,
            }}>
              <span style={{ fontSize: 13, color: T.textMuted }}>Reason</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: T.text, fontStyle: 'italic', textAlign: 'right', maxWidth: '60%' }}>
                {transaction.tpReason}
              </span>
            </div>
          )}

          {/* Date */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 24px', borderBottom: `1px solid ${T.borderLight}`,
          }}>
            <span style={{ fontSize: 13, color: T.textMuted }}>Date</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: T.text }}>
              {formatLongDate(transaction.date)}
            </span>
          </div>

          {/* Category */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 24px', gap: 12,
          }}>
            <span style={{ fontSize: 13, color: T.textMuted, flexShrink: 0 }}>Category</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {CATEGORIES.map((cat) => {
                const isActive = transaction.tpCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => handleSelect(cat.key)}
                    disabled={saving}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 20,
                      border: 'none',
                      background: isActive ? T.primary : T.cream,
                      color: isActive ? '#fff' : T.textSecondary,
                      fontSize: 12,
                      fontWeight: isActive ? 600 : 400,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.5 : 1,
                      whiteSpace: 'nowrap',
                      transition: `background 200ms ${T.easeOut}, color 200ms ${T.easeOut}`,
                      fontFamily: T.fontFamily,
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 24px 24px',
          borderTop: `1px solid ${T.borderLight}`,
          fontSize: 12, color: T.textMuted, fontFamily: T.fontFamily,
        }}>
          Changes here recalculate burn allocation.
        </div>

        {/* Toast */}
        {toast && (
          <div style={{
            textAlign: 'center', fontSize: 13, fontWeight: 600,
            color: toast === 'Updated' ? T.primary : T.warning,
            padding: '0 24px 8px',
          }}>
            {toast}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

export default CategorizationSheet;
